import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchMyTickets } from '@/lib/support/tickets';
import { PlusIcon, LifebuoyIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/cn';
import type { TicketStatus, TicketPriority } from '@/types/admin';

const STATUS_STYLES: Record<TicketStatus, string> = {
  OPEN: 'bg-sky-100 text-sky-700',
  PENDING: 'bg-amber-100 text-amber-700',
  IN_PROGRESS: 'bg-violet-100 text-violet-700',
  RESOLVED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-gray-100 text-gray-700',
};
const PRIORITY_STYLES: Record<TicketPriority, string> = {
  LOW: 'bg-gray-100 text-gray-700',
  NORMAL: 'bg-sky-100 text-sky-700',
  HIGH: 'bg-amber-100 text-amber-700',
  URGENT: 'bg-rose-100 text-rose-700',
};

export default function SupportPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['support', 'tickets'],
    queryFn: fetchMyTickets,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <LifebuoyIcon className="h-6 w-6 text-primary-600" /> {t('support.title')}
        </h1>
        <Link
          to="/support/new"
          className="inline-flex items-center gap-1 rounded-lg bg-primary-700 px-3 py-2 text-sm font-medium text-white hover:bg-primary-800"
        >
          <PlusIcon className="h-4 w-4" /> {t('support.newTicket')}
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <p className="p-6 text-sm text-gray-500">{t('common.loading')}</p>
        ) : (data ?? []).length === 0 ? (
          <p className="p-6 text-sm text-gray-500">{t('support.noTickets')}</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2">{t('admin.columns.subject')}</th>
                <th className="px-4 py-2">{t('admin.columns.status')}</th>
                <th className="px-4 py-2">{t('admin.columns.priority')}</th>
                <th className="px-4 py-2">{t('admin.columns.updated')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data ?? []).map((tk) => (
                <tr key={tk.id}>
                  <td className="px-4 py-3">
                    <Link to={`/support/tickets/${tk.id}`} className="font-medium text-primary-700 hover:underline">
                      {tk.subject}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', STATUS_STYLES[tk.status])}>{t(`support.status.${tk.status}`)}</span></td>
                  <td className="px-4 py-3"><span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', PRIORITY_STYLES[tk.priority])}>{t(`support.priority.${tk.priority}`)}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(tk.updatedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
